import { useState, useEffect, useRef, useCallback } from 'react';
import { getRulesetType } from '../services/api/rulesetApi';
import { getCustomer } from '../services/api/customerApi';

/**
 * Custom hook for fetching rule set and customer data for Rule Set Detail View
 * @param {string} customerId - The ID of the customer
 * @param {string} rulesetId - The ID of the rule set
 * @returns {Object} Rule set and customer detail state and controls
 */
export const useRuleSetDetail = (customerId, rulesetId) => {
  const [ruleset, setRuleset] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use ref to prevent duplicate API calls in StrictMode
  const hasFetched = useRef(false);
  const currentCustomerId = useRef(customerId);
  const currentRulesetId = useRef(rulesetId);

  useEffect(() => {
    // Reset when IDs change
    if (currentCustomerId.current !== customerId || currentRulesetId.current !== rulesetId) {
      currentCustomerId.current = customerId;
      currentRulesetId.current = rulesetId;
      hasFetched.current = false;
      setRuleset(null);
      setCustomer(null);
    }

    if (!hasFetched.current && customerId && rulesetId) {
      hasFetched.current = true;
      fetchRuleSetDetail();
    }
  }, [customerId, rulesetId]);

  const fetchRuleSetDetail = async () => {
    if (!customerId || !rulesetId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch both customer and rule set in parallel
      const [customerResponse, rulesetResponse] = await Promise.all([
        getCustomer(customerId),
        getRulesetType(rulesetId)
      ]);

      const customerData = customerResponse.data;
      const rulesetData = rulesetResponse.data;

      // Validate that rule set belongs to customer
      if (rulesetData.customerId !== customerId) {
        throw new Error('Rule set does not belong to the specified customer');
      }

      setCustomer(customerData);
      setRuleset(rulesetData);
    } catch (err) {
      // Handle 404 errors gracefully
      if (err.response?.status === 404) {
        setError('Rule set or customer not found');
      } else if (err.message === 'Rule set does not belong to the specified customer') {
        setError(err.message);
      } else {
        setError(err.response?.data?.message || 'Failed to fetch rule set details');
      }
      console.error('Error fetching rule set detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshRuleSet = useCallback(async () => {
    hasFetched.current = false;
    await fetchRuleSetDetail();
  }, [customerId, rulesetId]);

  return {
    ruleset,
    customer,
    loading,
    error,
    refreshRuleSet
  };
};
